import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import type { MatrixInput } from "../lib/coverage";
import {
  coverageRowsFor, linkedIdsToFetch, setsByExerciseRow, shownWorkoutIds, weekEntries,
} from "../lib/coverageWeek";
import { myId } from "../lib/me";
import {
  deferredCells, resolve as resolveDeferrals,
  type DeferralResolution, type StoredDeferral,
} from "../lib/deferrals";

/* Reading one client's week, once, for both screens that need it.
 *
 * The pure conversions live in `lib/coverageWeek.ts` and are executed by `coverageWeek.test.ts`.
 * What is here is the awaiting — the part with nothing to assert about it and no way to run it
 * on this Mac — kept in `routes/` rather than `lib/` so the untested-rule ratchet's scope stays a
 * statement about PURE modules rather than a list with an exception in it.
 *
 * ⚠️ FLAT QUERIES RATHER THAN NESTED EMBEDS, DELIBERATELY. A PostgREST embed comes back as an
 * OBJECT or an ARRAY depending on the relationship it infers, and `.name` on an array is
 * `undefined` — CLAUDE.md records a whole column that would have read "Untitled workout" with
 * nothing going red. These joins have nothing to gain from nesting.
 *
 * ⚠️ READ-ONLY. Nothing here writes, and nothing in the generator reads what it returns — his §8
 * puts that at step 5 and warns against attempting steps 1–5 in one pass.
 */

export type CoverageWeek =
  | { readonly status: "loading" }
  | { readonly status: "error"; readonly message: string }
  | {
      readonly status: "ready";
      readonly input: MatrixInput;
      readonly from: string;
      readonly to: string;
      readonly hiddenSkipped: number;
      /** Coverage rows the whole library holds — the denominator behind "described". */
      readonly libraryRowCount: number;
      /** Rows dropped for naming no coach exercise. Zero today; said out loud anyway. */
      readonly skippedRows: number;
      /**
       * Every DESCRIBED exercise's name, not only the week's.
       *
       * 🔴 THIS IS WHAT LETS THE GAP REPORT FINISH HIS §6.2's SENTENCE. *"Here is the specific
       * range you are not training, **and here is what fills it**"* needs a name for an exercise
       * the athlete is by definition NOT doing, so a map built from the week can never supply one.
       */
      readonly libraryNames: ReadonlyMap<string, string>;
      /**
       * The athlete's own declarations, resolved.
       *
       * Carried BESIDE `input.deferrals` rather than instead of it: the matrix wants one entry
       * per CELL and a reader wants one per DECLARATION (to show the reason once, and to say
       * when a review is overdue). Deriving either from the other on a screen would put the
       * expansion rule in a `.map()` nothing executes.
       */
      readonly deferrals: DeferralResolution;
    };

export function useCoverageWeek(clientId: string, from: string, to: string): CoverageWeek {
  const [state, setState] = useState<CoverageWeek>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setState({ status: "loading" });
      const fail = (m: string) => {
        if (!cancelled) setState({ status: "error", message: m });
      };
      try {
        /* 🔴 THE LIBRARY IS THE WHOLE DESCRIBED CATALOGUE AND IS READ FIRST, ON PURPOSE.
           Restricting it to the week's own exercises is what §11.3 measured as making the audit
           set structurally unable to report a hole — servable ∩ empty is empty by construction
           when the library IS the week. Reading it first also means an empty week still knows
           what the library could have filled. */
        const cov = await supabase
          .from("exercise_coverage")
          .select("coach_exercise_id, joint_action, zone, role, note")
          /* ⚠️ THE 1,000-ROW GUARD, and it narrows NOTHING this screen shows. The table also
             holds the engine catalogue's rows — keyed on `exercise_id`, `coach_exercise_id`
             NULL — and since the 2026-08-31 coverage pass that is 2,960 of 3,443 live rows.
             `coverageRows` already skips them client-side; asking for them at all means an
             unfiltered select of 3,443 rows against PostgREST's default 1,000-row cap, which
             this project has already met once ("the first verify read 1,000 of 5,445",
             DECISIONS.md) and which truncates in silence. Filtering server-side leaves the 483
             coach-keyed rows, under the cap. Found by the 2026-09-02 audit; pinned by
             joint-action-5's ruler 7 as a literal. */
          .not("coach_exercise_id", "is", null);
        if (cov.error !== null) return fail(cov.error.message);

        /* 🔴 F15-2 — THIS COACH'S ROWS AND THEIR CATALOGUE LINKS, then the catalogue half for the linked rows
           that have no description of their own, FILTERED SERVER-SIDE by those ids (`linkedIdsToFetch`) —
           never an unfiltered select of the 3,008 catalogue-keyed rows. Named by owner (`myId`), the
           library-list rule. `coverageRowsFor` is the one resolver; its twin is the phone's. */
        const mine = await supabase
          .from("coach_exercises").select("id, name, catalog_exercise_id").eq("coach_id", await myId());
        if (mine.error !== null) return fail(mine.error.message);
        const links = mine.data ?? [];
        const linkedIds = linkedIdsToFetch(cov.data ?? [], links);
        const cat = linkedIds.length === 0
          ? { data: [], error: null }
          : await supabase
              .from("exercise_coverage")
              .select("exercise_id, joint_action, zone, role, note")
              .in("exercise_id", linkedIds);
        if (cat.error !== null) return fail(cat.error.message);
        const { rows, skippedWithoutCoachExercise } = coverageRowsFor(cov.data ?? [], cat.data ?? [], links);

        // Names for every described exercise, so a gap can name what would fill it. Bounded by
        // the described library, not by the catalogue.
        const describedIds = [...new Set(rows.map((r) => r.exerciseId))];
        const known = new Map(links.map((n) => [n.id, n.name]));
        const unnamed = describedIds.filter((id) => !known.has(id));
        const libNames = unnamed.length === 0
          ? { data: [] as { id: string; name: string }[], error: null }
          : await supabase.from("coach_exercises").select("id, name").in("id", unnamed);
        if (libNames.error !== null) return fail(libNames.error.message);
        const libraryNames = new Map([
          ...describedIds.flatMap((id) => (known.has(id) ? [[id, known.get(id) ?? ""] as const] : [])),
          ...(libNames.data ?? []).map((n) => [n.id, n.name] as const),
        ]);

        /* 🔴 THE DECLARED ZEROS. `coverage.ts` shipped `DEFERRALS_HAVE_NO_HOME` because there
           was nowhere to keep one; `profiles.deferred_actions` is that home, and this is the
           read that makes the rule reachable.

           ⚠️ THE COLUMN IS NAMED IN THE `select`, WHICH IS NOT THE SAME CLAIM AS THE TYPE
           KNOWING ABOUT IT — v6-12 lost a whole feature to a rule about a column the query
           never asked for, and the decoding key was still present and still green. A ruler
           pins this literal.

           Read through the SAME row policy that already lets a coach read `injuries_data`
           (`profiles_coach_reads_clients`); this landing wrote no policy. A profile the coach
           cannot read returns no row, which resolves to no declarations — the honest answer,
           and the same one as an athlete who has declared nothing. */
        const prof = await supabase
          .from("profiles").select("deferred_actions").eq("id", clientId).maybeSingle();
        if (prof.error !== null) return fail(prof.error.message);
        const stored = Array.isArray(prof.data?.deferred_actions)
          ? (prof.data.deferred_actions as unknown as StoredDeferral[])
          : [];
        const deferrals = resolveDeferrals(stored);

        const asg = await supabase
          .from("coach_assignments")
          .select("workout_id, scheduled_date, hidden_from_client")
          .eq("client_id", clientId)
          .gte("scheduled_date", from)
          .lte("scheduled_date", to);
        if (asg.error !== null) return fail(asg.error.message);

        const { workoutIds, hiddenSkipped } = shownWorkoutIds(asg.data ?? []);
        const ready = (week: MatrixInput["week"]) => {
          if (cancelled) return;
          setState({
            status: "ready", from, to, hiddenSkipped,
            libraryRowCount: rows.length,
            skippedRows: skippedWithoutCoachExercise,
            libraryNames,
            deferrals,
            // One entry per CELL — the action crossed with the zones it carries, read from the
            // generated list rather than from anything the athlete's row stores.
            input: { week, rows, deferrals: deferredCells(deferrals) },
          });
        };

        if (workoutIds.length === 0) return ready([]);

        const secs = await supabase
          .from("coach_workout_sections").select("id, workout_id").in("workout_id", workoutIds);
        if (secs.error !== null) return fail(secs.error.message);
        const sectionIds = (secs.data ?? []).map((s) => s.id);
        if (sectionIds.length === 0) return ready([]);

        const exs = await supabase
          .from("coach_workout_exercises").select("id, exercise_id").in("section_id", sectionIds);
        if (exs.error !== null) return fail(exs.error.message);
        const exRows = exs.data ?? [];

        const sets = await supabase
          .from("coach_workout_sets").select("workout_exercise_id")
          .in("workout_exercise_id", exRows.map((e) => e.id));
        if (sets.error !== null) return fail(sets.error.message);

        const libraryIds = [
          ...new Set(exRows.map((e) => e.exercise_id).filter((x): x is string => x !== null)),
        ];
        const names = await supabase
          .from("coach_exercises").select("id, name").in("id", libraryIds);
        if (names.error !== null) return fail(names.error.message);

        ready(weekEntries(
          exRows,
          setsByExerciseRow(sets.data ?? []),
          new Map((names.data ?? []).map((n) => [n.id, n.name])),
        ));
      } catch (e) {
        fail(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, from, to]);

  return state;
}
